/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, TextInput } from '@coursera/coursera-ui';

import CodeBlockV2 from 'bundles/code-evaluator/components/CodeBlockV2';
import Modal from 'bundles/phoenix/components/Modal';
import type { OptionProps, UserSuppliedObject } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/SessionConfigurationEditorModal';

// TODO: Move this to a constants file.
// Do not remove the whitespace inside this string.
const DEFAULT_CONFIG_JSON = `{
  
}`;

const styles = {
  alertContainer: css`
    .bt3-alert {
      padding: 15px;
      margin-bottom: 20px;
      border: 1px solid transparent;
      border-radius: 4px;
    }

    .bt3-alert h4 {
      margin-top: 0;
      color: inherit;
    }

    .bt3-alert > p,
    .bt3-alert > ul {
      margin-bottom: 0;
    }

    .bt3-alert > p + p {
      margin-top: 5px;
    }

    .bt3-alert-danger {
      background-color: var(--cds-color-feedback-error-invert-weak);
      border-color: var(--cds-color-red-100);
      color: var(--cds-color-red-800);
    }

    .bt3-alert-danger hr {
      border-top-color: var(--cds-color-red-100);
    }
  `,
};

type Props = {
  name: string;
  title: string;
  onClose: () => void;
  session?: OptionProps;
  onSaveButtonClick: (session: OptionProps | undefined) => void;
};

type State = {
  label: string;
  configJson: string;
  errors?: Array<string>;
};

class SessionConfigurationEditorModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { session } = props;
    let label: string;
    let configJson: string;

    if (session) {
      label = session.label;
      configJson = JSON.stringify(session.config, null, 2);
    } else {
      label = '';
      configJson = DEFAULT_CONFIG_JSON;
    }

    this.state = {
      label,
      configJson,
      errors: [],
    };
  }

  onSaveButtonClick = () => {
    const { onSaveButtonClick } = this.props;

    const { session, errors } = this.validate();
    this.setState({ errors });

    if (!errors) {
      onSaveButtonClick(session);
    }
  };

  onNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ label: event.target.value });
  };

  onEditorChange = (configJson: string) => {
    this.setState({ configJson });
  };

  validate(): { session?: OptionProps | undefined; errors?: Array<string> } {
    const { label, configJson } = this.state;
    const errors: Array<string> = [];
    let config: UserSuppliedObject = {};

    // Make sure the session has a name so we can render something in the dropdown list.
    if (!(label || '').trim()) {
      errors.push(_t('Please enter a name'));
    }

    // Make sure this is valid JSON before allowing it to be saved.
    try {
      config = JSON.parse(configJson);
    } catch (e) {
      errors.push(e.toString());
    }

    if (errors.length > 0) {
      return { errors };
    }

    // Everything looks good.
    return {
      session: {
        label,
        config,
      },
    };
  }

  render() {
    const { name, title, onClose } = this.props;
    const { errors, label, configJson } = this.state;

    return (
      <Modal className="rc-SessionConfigurationEditorModal" modalName={name} handleClose={onClose}>
        <h3 className="modal-title">{title}</h3>

        <div className="modal-content">
          <div className="m-b-1">
            <TextInput
              componentId="SessionConfigurationEditorModalName"
              placeholder={_t('Name')}
              value={label}
              onChange={this.onNameInputChange}
            />
          </div>

          <CodeBlockV2
            readOnly={false}
            codeLanguage="json"
            expression={configJson}
            onChange={this.onEditorChange}
            maxLines={20}
          />
        </div>

        {errors && errors.length > 0 && (
          <div css={styles.alertContainer}>
            <div className="error-message bt3-alert bt3-alert-danger">
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <Button type="primary" label={_t('Save')} onClick={this.onSaveButtonClick} />
          <Button type="noStyle" label={_t('Cancel')} onClick={onClose} />
        </div>
      </Modal>
    );
  }
}

export default SessionConfigurationEditorModal;
